"use client";

import { Any, IMixpanelClient, MIXPANEL_PLATFORM, MixpanelProperties } from "@cede/types";
import { firstLetterToUpperCase } from "@cede/utils";
import { normalizeOsName } from "./utils";
import { clientConfig } from "@/lib/config/client-config";
import mixpanel from "mixpanel-browser";
import { UAParser } from "ua-parser-js";

const DISTINCT_ID_KEY = "645CSL0VAG";
const DEVICE_ID_KEY = "896JHG8937";
const UTM_PROPERTIES = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

const readUtmProperties = (search: string): MixpanelProperties => {
  const params = new URLSearchParams(search);
  return Object.fromEntries(
    UTM_PROPERTIES.flatMap((key) => {
      const value = params.get(key);
      return value ? [[key, value]] : [];
    }),
  );
};

// Read at module load, on the landing page: consent may start Mixpanel only after a client-side navigation.
// Registered once consent starts Mixpanel, so they persist across reloads.
const landingUtmProperties = typeof window === "undefined" ? {} : readUtmProperties(window.location.search);

export class MixpanelClient implements IMixpanelClient {
  private static instance: MixpanelClient;
  private initialized = false;
  private legacyId: string | null = null;

  /**
   * This is used to identify if the user is identified.
   * This is used to avoid storing the distinct id in the local storage if it
   * is the defi address.
   */
  private isIdentified = false;

  // Properties for client info
  private browserName = "";
  private browserVersion = "";
  private osName = "";
  private screenHeight = 0;
  private screenWidth = 0;

  private constructor() {}

  /**
   * Deferred until the visitor allows analytics: `mixpanel.init` writes storage
   * and opens a network connection. Every other method is a no-op until this
   * runs, so no call site needs to know about consent.
   */
  public start(): void {
    if (this.initialized) return;

    const token = clientConfig.mixpanelToken;
    const proxyDomain = clientConfig.mixpanelProxyDomain;

    if (!token || !proxyDomain) {
      // eslint-disable-next-line no-console
      console.warn("Mixpanel token or proxy domain is missing! Check clientConfig.");
      return;
    }

    // No session replay here: the 20K/month allowance is org-wide across every
    // Mixpanel project, and it is spent on Arena.
    mixpanel.init(token, {
      cross_site_cookie: true,
      api_host: proxyDomain,
    });
    if (Object.keys(landingUtmProperties).length > 0) {
      mixpanel.register(landingUtmProperties);
    }

    this.initialized = true;

    const { browser, os } = UAParser(window.navigator.userAgent);

    this.browserName = firstLetterToUpperCase(browser.name ?? "");
    this.browserVersion = browser.version ?? "";
    // Normalize macOS name for Mixpanel consistency
    this.osName = normalizeOsName(os.name);
    this.screenHeight = window.screen.height;
    this.screenWidth = window.screen.width;
  }

  public static getInstance(): MixpanelClient {
    if (!MixpanelClient.instance) {
      MixpanelClient.instance = new MixpanelClient();
    }
    return MixpanelClient.instance;
  }

  public setLegacyId(legacyId: string | null): void {
    this.legacyId = legacyId;
  }

  public identify(id: string): void {
    if (!this.initialized) return;
    mixpanel.identify(id);
    this.isIdentified = true;

    if (this.legacyId) {
      this.unionPeople({
        legacyId: this.legacyId,
      });
    }
  }

  public unionPeople(props: MixpanelProperties): void {
    if (!this.initialized) return;
    mixpanel.people.union(props);
  }

  public setPeople(props: MixpanelProperties): void {
    if (!this.initialized) return;
    mixpanel.people.set(props);
  }

  public setPeopleOnce(props: MixpanelProperties): void {
    if (!this.initialized) return;
    mixpanel.people.set_once(props);
  }

  public register(properties: MixpanelProperties): void {
    if (!this.initialized) return;
    mixpanel.register(properties);
  }

  public registerOnce(properties: MixpanelProperties): void {
    if (!this.initialized) return;
    mixpanel.register_once(properties);
  }

  public getProperty(propertyName: string): Any {
    if (!this.initialized) return null;
    return mixpanel.get_property(propertyName);
  }

  public getDeviceId(): string | null {
    if (!this.initialized) return null;
    const deviceIdFromLocalStorage = localStorage.getItem(DEVICE_ID_KEY);
    if (deviceIdFromLocalStorage) {
      return deviceIdFromLocalStorage;
    }
    const deviceId = mixpanel.get_property("$device_id");
    if (deviceId) {
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }
    return deviceId;
  }

  /**
   * Returns client properties for Mixpanel events.
   */
  public getClientProperties(): MixpanelProperties {
    if (!this.initialized) return {};

    // Get dynamic values that may change during the session
    const currentUrl = window.location.href;
    const initialReferrer = mixpanel.get_property("$initial_referrer");
    const initialReferringDomain = initialReferrer
      ? initialReferrer === "$direct"
        ? "$direct"
        : new URL(initialReferrer).hostname
      : "";

    const platform = mixpanel.get_property("platform");
    const deviceId = this.getDeviceId();
    // Events go through our API proxy, not mixpanel.track, so super properties must be copied in.
    const utmProperties = Object.fromEntries(
      UTM_PROPERTIES.map((key) => [key, mixpanel.get_property(key)]).filter(([, value]) => value != null),
    );

    return {
      $browser: this.browserName,
      $browser_version: this.browserVersion,
      $current_url: currentUrl,
      $initial_referrer: initialReferrer,
      $initial_referring_domain: initialReferringDomain,
      $os: this.osName,
      $screen_height: this.screenHeight,
      $screen_width: this.screenWidth,
      $device_id: deviceId,
      ...utmProperties,
      // Custom properties
      platform,
    };
  }

  public trackWalletConnect(): void {
    if (!this.initialized) return;

    const currentDate = new Date().toISOString();

    // Note: Sign In/Log In events are now tracked reliably on the backend
    // via the signin endpoint to avoid browser blocking issues.
    // This method now only handles people properties that are client-side specific.

    this.setPeople({
      lastLoginDate: currentDate,
    });
  }

  public reset(): void {
    this.isIdentified = false;
    // Before the guard: on a refusal Mixpanel was never started, and these ids
    // from an earlier visit are exactly what has to go.
    localStorage.removeItem(DISTINCT_ID_KEY);
    localStorage.removeItem(DEVICE_ID_KEY);

    if (!this.initialized) return;
    mixpanel.reset();
  }

  public registerPlatform(isTma: boolean): void {
    if (!this.initialized) return;

    this.register({
      platform: isTma ? MIXPANEL_PLATFORM.TELEGRAM : MIXPANEL_PLATFORM.WEB,
    });
  }

  public getDistinctId(): string | null {
    if (!this.initialized) return null;
    if (!this.isIdentified) {
      const distinctIdFromLocalStorage = localStorage.getItem(DISTINCT_ID_KEY);
      if (distinctIdFromLocalStorage) {
        return distinctIdFromLocalStorage;
      }
    }

    const distinctId = mixpanel.get_distinct_id();

    if (distinctId && !this.isIdentified) {
      localStorage.setItem(DISTINCT_ID_KEY, distinctId);
    }

    return distinctId;
  }
}

export const mixpanelClient = MixpanelClient.getInstance();
